import { memo } from 'react';
import classNames from 'classnames';
import { Typography } from '../../../../../components/typography/typography';
import { AssignUnitSelect } from '../../../assign-unit-select/assign-unit-select';
import { AssignedUnits } from '../../../assigned-units/assigned-units';
import { ProductTrackingType } from '../../../../../clients/quipli-service/product/product';
import { AssignedQuantity } from '../../../assigned-quantity/assigned-quantity';
import { OrderReturnStatus, OrderStatus, OrderType } from '../../../../../clients/quipli-service/order/order';
import { OrderDocumentType } from '../../../../../clients/quipli-service/order/order';
import styles from './product-item.module.css';
import { ProductItemState } from '../../hooks/use-product-item-data';
import { ProductItemUnitsReturn } from '../../hooks/use-product-item-units';
import deepEqual from 'deep-equal';
import SkeletonLoader from '../../../../../components/skeleton-loader/skeleton-loader';

interface ProductItemUnitsProps
  extends Pick<
    ProductItemState,
    | 'isLoading'
    | 'trackingType'
    | 'isRental'
    | 'isEditing'
    | 'isNewProduct'
    | 'productId'
    | 'quantity'
    | 'units'
    | 'returnStatus'
    | 'initialStock'
    | 'startDate'
    | 'endDate'
    | 'id'
  > {
  orderId?: number;
  orderDocumentType: OrderDocumentType;
  locationId: number;
  saleSerialTrackingEnabled: boolean;
  qtyAvailable?: number;
  showQtyWarning: boolean;
  orderStatus: OrderStatus;
  updateUnits: ProductItemUnitsReturn['updateUnits'];
}

export const ProductItemUnits = memo<ProductItemUnitsProps>(
  ({
    trackingType,
    isLoading,
    isRental,
    isEditing,
    isNewProduct,
    qtyAvailable,
    showQtyWarning,
    saleSerialTrackingEnabled,
    orderDocumentType,
    orderId,
    productId,
    id,
    locationId,
    quantity,
    startDate,
    endDate,
    units,
    returnStatus,
    initialStock,
    orderStatus,
    updateUnits,
  }) => {
    // Render loading state
    if (isLoading) {
      return <SkeletonLoader style={{ width: '100%', height: '38px' }} />;
    }

    if (trackingType === ProductTrackingType.NonInventory) {
      return (
        <Typography
          type="text-sm"
          weight="regular"
          className={classNames(styles.fieldContentText)}
          dataCy="assign-unit-na"
        >
          N/A
        </Typography>
      );
    }

    if (trackingType === ProductTrackingType.Quantity || (!saleSerialTrackingEnabled && !isRental)) {
      return (
        <AssignedQuantity
          isEditing={isEditing}
          quantity={quantity}
          isRental={isRental}
          qtyAvailable={qtyAvailable}
          stock={isRental ? initialStock.rentalStock : initialStock.saleStock}
          showQtyWarning={showQtyWarning}
          className={styles.fieldContentText}
        />
      );
    }

    // FIXME: it looks like we cant assign units to a new product in the order contract, but we need to allow this when refactoring the create order workflow
    if (isNewProduct) {
      return null;
    }

    // Show unit assignment when order is in a state that allows unit assignment
    // For rentals: when return status is not started
    // For sales: when status is not approved
    const canAssignUnitsToOrder =
      (isRental && returnStatus === OrderReturnStatus.NotStarted) ||
      (!isRental && orderStatus !== OrderStatus.Approved);

    if (canAssignUnitsToOrder) {
      return (
        <AssignUnitSelect
          locationId={locationId}
          uniqueId={`${productId}`}
          productId={productId}
          quantity={quantity}
          startDate={startDate!}
          endDate={endDate}
          selectedUnitIds={units}
          disabled={orderDocumentType !== OrderDocumentType.Order}
          onChange={updateUnits}
          orderProductType={isRental ? OrderType.Rental : OrderType.Sale}
        />
      );
    }

    if (orderId) {
      return <AssignedUnits locationId={locationId} orderId={orderId} orderProductId={parseInt(id)} />;
    }

    return null;
  },
  (prevProps, nextProps) => {
    return (
      prevProps.trackingType === nextProps.trackingType &&
      prevProps.isLoading === nextProps.isLoading &&
      prevProps.isEditing === nextProps.isEditing &&
      prevProps.qtyAvailable === nextProps.qtyAvailable &&
      prevProps.showQtyWarning === nextProps.showQtyWarning &&
      prevProps.saleSerialTrackingEnabled === nextProps.saleSerialTrackingEnabled &&
      prevProps.productId === nextProps.productId &&
      prevProps.quantity === nextProps.quantity &&
      prevProps.units.length === nextProps.units.length &&
      prevProps.returnStatus === nextProps.returnStatus &&
      prevProps.orderStatus === nextProps.orderStatus &&
      deepEqual(prevProps.initialStock, nextProps.initialStock)
    );
  },
);

ProductItemUnits.displayName = 'ProductItemUnits';
